<script setup lang="ts">
import type {
    BenefitGroups,
    BenefitGroupsData,
} from '~~/server/api/insurance/product'

export interface InsuranceServicesProps {
    data: BenefitGroups
}

const highlighteTrashold = 3
const props = defineProps<InsuranceServicesProps>()
const highlightedBenefit = computed<BenefitGroupsData | null>(
    (): BenefitGroupsData | null => {
        // just thrid item should by highlighted
        if (props.data.data.length < highlighteTrashold) return null
        // return last item of the array
        return props.data.data.slice(-1).pop() as BenefitGroupsData
    }
)
const benefits = computed<BenefitGroupsData[]>((): BenefitGroupsData[] => {
    // return full array if there is no highlighted
    if (props.data.data.length < highlighteTrashold) return props.data.data
    // return array withou the last highlighte
    return props.data.data.slice(0, -1)
})

const { data: gaEventData, sendEvent } = useTracker()

const handleClick = ({
    title,
    url,
}: {
    title: string
    url: string | undefined
}): void => {
    sendEvent({
        data: {
            ...gaEventData,
            position: props.data.title,
            title,
            click: url as string,
        },
    })
}
</script>

<template>
  <section>
    <Box
      decor-position="top-right"
      color="yellow"
      class="py-15 rounded align-items-center px-2"
    >
      <BoxHeader
        :heading="props.data.title"
        heading-level="h2"
        :subtitle="props.data.subtitle"
      >
        <template #tag>
          <Tag
            class="text-uppercase"
            :decor="false"
            size="small"
            background-color="yellow-40"
          >
            {{ props.data.tag }}
          </Tag>
        </template>
      </BoxHeader>
      <div class="row justify-content-center">
        <div
          v-for="(item, index) in benefits"
          :key="index"
          class="col-12 mb-lg-0"
          :class="{
            'mb-2 mb-md-6': index === 0,
            'col-md-6': benefits.length < 3,
            'col-lg-3': benefits.length === 2 && highlightedBenefit,
          }"
        >
          <Card
            :background-color="
              !highlightedBenefit &&
                benefits.length === 2 &&
                index % 2 !== 0
                ? 'yellow-20'
                : 'white'
            "
            classes="p-6 rounded h-100"
          >
            <h3 class="epsilon mb-7">
              {{ item.title }}
            </h3>
            <ul class="row">
              <li
                v-for="(value, index2) in item.benefits"
                :key="index2"
                class="fs-smaller d-flex align-items-center col-12 mb-4 position-relative"
                :class="{
                  'col-md-6': !highlightedBenefit,
                }"
              >
                <WidgetInsuranceServicesIcon
                  v-if="value.icon"
                  :icon="value.icon"
                />
                <a
                  v-if="value.url"
                  :href="value.url"
                  class="stretched-link text-decoration-underline"
                  :aria-label="`${item.title}, ${value.description}`"
                  @click="
                    handleClick({
                      title: value.description,
                      url: value.url,
                    })
                  "
                >
                  {{ value.description }}
                </a>
                <template v-else>
                  {{ value.description }}
                </template>
              </li>
            </ul>
          </Card>
        </div>
        <div
          v-if="highlightedBenefit"
          class="col-12 col-lg-6 mt-2 mt-md-6 mt-lg-0"
        >
          <Card
            background-color="yellow-20"
            classes="p-6 rounded h-100"
          >
            <h3 class="epsilon mb-7">
              {{ highlightedBenefit.title }}
            </h3>
            <ul class="row">
              <li
                v-for="(
                  item, index
                ) in highlightedBenefit.benefits"
                :key="`hb-${index}`"
                class="fs-smaller d-flex align-items-center col-12 col-md-6 mb-4 position-relative"
              >
                <WidgetInsuranceServicesIcon
                  v-if="item.icon"
                  :icon="item.icon"
                />
                <a
                  v-if="item.url"
                  :href="item.url"
                  class="stretched-link text-decoration-underline"
                  :aria-label="`${highlightedBenefit.title}, ${item.description}`"
                  @click="
                    handleClick({
                      title: item.description,
                      url: item.url,
                    })
                  "
                >
                  {{ item.description }}
                </a>
                <template v-else>
                  {{ item.description }}
                </template>
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </Box>
  </section>
</template>
